import { HttpClient } from "aurelia-fetch-client";
import { autoinject } from "aurelia-framework";
import { buildQueryString } from "aurelia-path";

@autoinject
export class IdentityClient {
  private _client: HttpClient;

  public constructor(client: HttpClient) {
    this._client = client;
  }

  public build<TResponse>(path: string): IdentityClientChain {
    return new IdentityClientChain(path, this._client);
  }
}

export class IdentityClientChain {
  private _path: string;
  private _init: RequestInit;
  private _client: HttpClient;

  public constructor(path: string, client: HttpClient) {
    this._path = path;
    this._init = {
      method: "GET"
    };
    this._client = client;
  }

  public useQueryString<TParam>(param: TParam): IdentityClientChain {
    if (param == null) {
      return this;
    }

    let concat = this._path.indexOf("?") != -1;
    let anchorChar = concat ? "&" : "?";

    let queryString = buildQueryString(param);
    this._path += anchorChar + queryString;
    return this;
  }

  public useMethod(method: string): IdentityClientChain {
    this._init.method = method;
    return this;
  }

  public useBody<T>(body: T): IdentityClientChain {
    let json = JSON.stringify(body);
    this._init.body = json;
    return this;
  }

  public useRecaptchaValidation<T>(response: string): IdentityClientChain {
    this._init.headers = { "g-recaptcha-response": response };
    return this;
  }

  public async fetch<TResponse>(
    throwOnError: boolean = true
  ): Promise<TResponse> {
    let response = await this._client.fetch(this._path, this._init);
    if (!response.ok) {
      if (throwOnError) {
        throw new IdentityClientHttpError(
          response.status,
          response.statusText,
          response
        );
      }
      return null;
    }

    let json = await response.json();
    return json as TResponse;
  }

  public async fetchOkErrors<TResponse>(
    throwOnError: boolean = true
  ): Promise<TResponse> {
    let response = await this._client.fetch(this._path, this._init);
    if (!response.ok) {
      if (throwOnError) {
        throw new IdentityClientHttpError(
          response.status,
          response.statusText,
          response
        );
      } else if(response.status == 400 || response.status == 403) {
          let json = await response.json();
          throw new IdentityClientHttpError(response.status, response.statusText, json)
      }
      return null;
    }

    let json = await response.json();
    return json as TResponse;
  }

  public async fetchAllowErrors(): Promise<object> {
    let response = await this._client.fetch(this._path, this._init);
    if (response.status == 400) {
      let json = await response.json();
      throw new IdentityClientHttpError(response.status, response.statusText, json);
    } else if(response.status !== 204 || !response.ok) {
      throw new IdentityClientHttpError(response.status, response.statusText, response);
    }
    
    return {};
  }

  public async fetchNoContent(throwOnError: boolean = true): Promise<object> {
    let response = await this._client.fetch(this._path, this._init);
    if (response.status !== 204) {
      if (throwOnError) {
        if(response.status === 403) {
          let json = await response.json();
          throw new IdentityClientHttpError(response.status, response.statusText, json)
        }
        else{
          throw new IdentityClientHttpError(
            response.status,
            `Expected '204 : No Content' but received '${response.status} : ${response.statusText}' instead.`,
            response
          );
        }
      }
      return null;
    }
    return {};
  }
}

export interface IIdentityClientOptions {
  path: string;
  method?: string;
  body?: any;
  query?: any;
}

export class IdentityClientHttpError {
  public statusMessage: string;
  public status: number;
  public response: Response;

  public constructor(
    status: number,
    statusMessage: string,
    response: Response
  ) {
    this.status = status;
    this.statusMessage = statusMessage;
    this.response = response;
  }
}

export interface IRedirectErrorContainer {
  redirectUrl: string;
  error?: string;
}